import { useState } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, Award, ShieldCheck, ArrowRight, MapPin, Phone, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Logo } from "@/components/Logo";

const FEATURES = [
  { icon: FlaskConical, t: "Profesjonalne środki", s: "Najwyższa jakość" },
  { icon: Award, t: "Doświadczenie", s: "Pasja do każdego detalu" },
  { icon: ShieldCheck, t: "Gwarancja jakości", s: "Twoje auto w dobrych rękach" },
];

const BookBtn = ({ testid, className = "" }) => (
  <Link to="/rezerwacja" data-testid={testid} className={`btn-y inline-flex items-center gap-2 px-7 py-3.5 text-sm ${className}`}>
    Zarezerwuj termin <ArrowRight size={16} />
  </Link>
);

const Nav = ({ onOpen }) => (
  <header className="absolute top-0 inset-x-0 z-20 max-w-7xl mx-auto px-5 sm:px-10 pt-6 flex items-center justify-between">
    <Logo className="h-16 sm:h-20" />
    <nav className="flex items-center gap-6 sm:gap-8 text-sm">
      <button data-testid="nav-about" onClick={() => onOpen("about")} className="text-white/80 hover:text-primary transition-colors">O nas</button>
      <button data-testid="nav-contact" onClick={() => onOpen("contact")} className="text-white/80 hover:text-primary transition-colors">Kontakt</button>
    </nav>
  </header>
);

const Hero = () => (
  <section className="relative h-[100svh] flex flex-col justify-end grain overflow-hidden">
    <img src="/img/bg-front.webp" alt="Czarne BMW w warsztacie" className="absolute inset-0 w-full h-full object-cover object-[65%_center]" />
    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/10" />
    <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-black/40" />
    <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-10 pb-10 sm:pb-14">
      <p className="eyebrow text-white/70 rise">Profesjonalny detailing</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mt-5 max-w-2xl rise d1">
        Twój samochód<br />zasługuje <span className="text-primary">na więcej.</span>
      </h1>
      <p className="text-sm sm:text-base text-white/75 mt-6 max-w-md rise d2">Wybierz usługę, termin i ciesz się perfekcyjnym wyglądem swojego auta. Rezerwacja w 3 prostych krokach.</p>
      <div className="mt-9 rise d3"><BookBtn testid="hero-book-button" /></div>
      <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mt-12 sm:mt-20 max-w-4xl rise d4">
        {FEATURES.map(({ icon: I, t, s }) => (
          <div key={t} className="flex items-center gap-4">
            <I className="text-primary shrink-0" size={28} strokeWidth={1.4} />
            <div><div className="text-sm font-semibold">{t}</div><div className="text-xs text-white/55">{s}</div></div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const AboutContent = () => (
  <>
    <DialogHeader className="text-left">
      <p className="eyebrow text-primary">O nas</p>
      <DialogTitle className="font-display text-3xl font-bold">Detal robi różnicę.</DialogTitle>
      <DialogDescription className="text-white/75 text-sm leading-relaxed pt-2">
        Doyebany Detailing to pasja do perfekcyjnie wykończonych aut. Pracujemy na sprawdzonych środkach i dbamy o każdy element — od felg po wnętrze. Przyjedź do naszego warsztatu lub zamów detailing mobilny.
      </DialogDescription>
    </DialogHeader>
    <BookBtn testid="about-book-button" className="mt-4 self-start" />
  </>
);

const ContactContent = () => (
  <>
    <DialogHeader className="text-left">
      <p className="eyebrow text-primary">Kontakt</p>
      <DialogTitle className="font-display text-3xl font-bold">Skontaktuj się z nami</DialogTitle>
      <DialogDescription className="sr-only">Adres, telefon i e-mail</DialogDescription>
    </DialogHeader>
    <div className="space-y-4 text-sm text-white/85 mt-2">
      <div data-testid="contact-address" className="flex items-center gap-3"><MapPin size={18} className="text-primary" /> [Adres warsztatu]</div>
      <div data-testid="contact-phone" className="flex items-center gap-3"><Phone size={18} className="text-primary" /> [Numer telefonu]</div>
      <div data-testid="contact-email" className="flex items-center gap-3"><Mail size={18} className="text-primary" /> <a href="mailto:doyebany.biuro@gmail.com" className="hover:text-primary">doyebany.biuro@gmail.com</a></div>
    </div>
  </>
);

export default function Landing() {
  const [open, setOpen] = useState(null);
  return (
    <div data-testid="landing-page" className="h-[100svh] overflow-hidden">
      <Nav onOpen={setOpen} />
      <Hero />
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent data-testid={`${open}-dialog`} className="panel border-primary/40 w-[calc(100%-2rem)] max-w-md flex flex-col bg-black/85 text-left">
          {open === "about" ? <AboutContent /> : <ContactContent />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
